import { createContext } from 'react'

import i18n from 'i18n-literally'

export const MODBUS_CONTROLLER_MODELS = new Set(['argus', 'ccms', 'generic_modbus', 'rhythm', 'rhythm_v2'])
Object.freeze(MODBUS_CONTROLLER_MODELS)

// these constants are used to figure out the context in which we operate with irrigation schedules
// possible contexts are: room-level, harvest-level, phase-level or a schedule template
export const ROOM_IRRIGATION_SCHEDULES = 'ROOM_LEVEL_IRRIGATION_SCHEDULES'
export const HARVEST_IRRIGATION_SCHEDULES = 'HARVEST_LEVEL_IRRIGATION_SCHEDULES'
export const PHASE_IRRIGATION_SCHEDULES = 'PHASE_LEVEL_IRRIGATION_SCHEDULES'
export const IRRIGATION_SCHEDULE_TEMPLATE = 'IRRIGATION_SCHEDULE_TEMPLATE'

export const IRRIGATION_SCHEDULE_STATUSES = Object.freeze({
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  PAUSED: 'PAUSED',
  PAUSING: 'PAUSING',
  UNCONFIRMED: 'UNCONFIRMED',
  PENDING: 'PENDING',
  REMOVING: 'REMOVING',
  ERROR: 'ERROR',
})

export const IRRIGATION_SCHEDULE_PENDING_STATUSES = Object.freeze({
  PAUSING: 'PAUSING',
  PENDING: 'PENDING',
  REMOVING: 'REMOVING',
})

export const IRRIGATION_CONTROLLER_PENDING_STATUSES = Object.freeze({
  CONFIGURING: 'CONFIGURING',
  NEW: 'NEW',
  PENDING: 'PENDING',
  RETRYING: 'RETRYING',
})

export const IRRIGATION_CONTROLLER_ERROR_STATUSES = Object.freeze({
  DUPLICATE_DEVICE: 'DUPLICATE DEVICE',
  ERROR: 'ERROR',
  INVALID_DEVICE: 'INVALID DEVICE',
  INVALID_FIRMWARE: 'INVALID FIRMWARE',
  INVALID_PASSWORD: 'INVALID PASSWORD',
  NO_HOSTNAME: 'NO HOSTNAME',
  UNABLE_TO_CONNECT: 'UNABLE TO CONNECT',
})

export const IRRIGATION_CONTROLLER_STATUSES = Object.freeze({
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  ...IRRIGATION_CONTROLLER_PENDING_STATUSES,
  ...IRRIGATION_CONTROLLER_ERROR_STATUSES,
})

export const HUMANIZED_IRRIGATION_CONTROLLER_STATUSES = Object.freeze({
  get ACTIVE() { return i18n`Active` },
  get INACTIVE() { return i18n`Inactive` },
  get CONFIGURING() { return i18n`Configuring` },
  get NEW() { return i18n`New` },
  get PENDING() { return i18n`Pending` },
  get RETRYING() { return i18n`Retrying` },
  get DUPLICATE_DEVICE() { return i18n`Duplicate Device` },
  get ERROR() { return i18n`Error` },
  get INVALID_DEVICE() { return i18n`Invalid Device` },
  get INVALID_FIRMWARE() { return i18n`Invalid Firmware` },
  get INVALID_PASSWORD() { return i18n`Invalid Password` },
  get NO_HOSTNAME() { return i18n`No Hostname` },
  get UNABLE_TO_CONNECT() { return i18n`Unable to Connect` }
})

export const IRRIGATION_CONTROLLER_PORT_STATUSES = Object.freeze({
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  INVALID: 'INVALID',
})

// helper array to calculate programs duration in seconds; also, select component options in duration and interval inputs
export const TIME_UNIT_OPTIONS = Object.freeze([
  {
    value: 1,
    get label() { return i18n`Sec` },
    toSeconds: v => v,
    get name() { return i18n`Second` },
    get symbol() { return i18n`s` },
  },
  {
    value: 2,
    get label() { return i18n`Min` },
    toSeconds: v => v * 60,
    get name() { return i18n`Minute` },
    get symbol() { return i18n`m` },
  },
  {
    value: 3,
    get label() { return i18n`Hrs` },
    toSeconds: v => v * 60 * 60,
    get name() { return i18n`Hour` },
    get symbol() { return i18n`h` },
  },
])
export const REPEAT_AFTER_UNIT_OPTIONS = Object.freeze(TIME_UNIT_OPTIONS.slice(1))

export const TIME_DURATION = 'TIME_DURATION'
export const PERCENTAGE_DURATION = 'PERCENTAGE_DURATION'
export const VOLUME_DURATION = 'VOLUME_DURATION'
export const TIME_DURATION_TYPE_VALUE = 1
// select component options above the duration input in the program card
export const durationTypes = Object.freeze([
  {
    type: TIME_DURATION,
    category: 'Time',
    value: TIME_DURATION_TYPE_VALUE,
    get label() { return i18n`For how long` },
    measurementUnit: '',
    inputOptions: TIME_UNIT_OPTIONS,
  },
  // NOTE: commenting %/ml types until we fully suport them
  // {
  //   type: PERCENTAGE_DURATION,
  //   category: 'Water Content',
  //   value: 2,
  // get () { return  label: i18n`How much (in %)` },
  //   measurementUnit: '%',
  //   name: 'Water Content',
  // },
  // {
  //   type: VOLUME_DURATION,
  //   category: 'Volume',
  //   value: 3,
  // get () { return  label: i18n`How much (in ml)` },
  //   measurementUnit: 'ml',
  //   name: 'Volume',
  // },
])

// helper functions to get correct select value on init form state of schedule editing
export const getDurationTypeSelectValue = (unitId, durationUnits) => {
  const durationUnitObj = durationUnits.find(unitObj => unitObj.id === unitId) || {}
  const durationType = durationTypes.find(dType => dType.category === durationUnitObj.category?.name) || {}
  return durationType.value || 1 // 1 is default type (time)
}
export const getDurationInputSelectValue = (unitId, durationUnits) => {
  const durationUnitObj = durationUnits.find(unitObj => unitObj.id === unitId) || {}
  const inputSelectOption = TIME_UNIT_OPTIONS.find(timeOption => timeOption.symbol === durationUnitObj.symbol) || {}
  if (!inputSelectOption.value) {
    const [defaultOption] = TIME_UNIT_OPTIONS
    return defaultOption.value
  }
  return inputSelectOption.value
}
export const getRepeatAfterSelectValue = (repeatAfterUnit, durationUnits) => {
  const durationUnitObj = durationUnits.find(unitObj => unitObj.id === repeatAfterUnit) || {}
  const inputSelectOption = REPEAT_AFTER_UNIT_OPTIONS.find(timeOption => timeOption.symbol === durationUnitObj.symbol) || {}
  if (!inputSelectOption.value) {
    const [defaultOption] = REPEAT_AFTER_UNIT_OPTIONS
    return defaultOption.value
  }
  return inputSelectOption.value
}

export const SUBSTRATE_CONFIGS = {
  SINGLE: 'SINGLE',
  STACKED: 'STACKED',
  MULTIPLE: 'MULTIPLE',
}

export const FIRMWARE_ALERT_MESSAGE = Object.freeze({
  get ABOUT_TO_LOSE_SUPPORT() { return i18n`Irrigation Firmware about to lose support.` },
  get MAY_NOT_WORK_AS_EXPECTED() { return i18n`Some functions may not work as expected.` },
  get NOT_SUPPORTED() { return i18n`Irrigation Firmware not supported.` },
  get NOT_YET_SUPPORTED() { return i18n`Irrigation Firmware not yet supported.` },
  get UPDATE_TO_LATEST_SUPPORTED() { return i18n`Please update to the latest supported Firmware.` },
})

export const NO_SEQUENTIAL_GROUP = -1

export const IRRIGATION_JOURNAL_EVENT_TYPES = Object.freeze({
  get SEQ_GROUP_CHANGE() { return i18n`Port Sequential Group Change(s)` },
  get PORT_ZONE_CHANGE() { return i18n`Port Zone Association Change(s)` },
  get EMERGENCY_SHOT() { return i18n`Emergency Shot` },
  get RUN_ONCE() { return i18n`Manual Irrigation` },
  get SCHEDULE_CHANGE() { return i18n`Irrigation Changed` },
  get SCHEDULE_PAUSED() { return i18n`Irrigation Paused` },
  get SCHEDULE_RESUMED() { return i18n`Irrigation Resumed` },
  get SCHEDULE_DELETED() { return i18n`Irrigation Schedule Deleted` },
  get IRRIGATION_STARTED() { return i18n`Irrigation Started` },
  get IRRIGATION_COMPLETED() { return i18n`Irrigation Completed` },
})

export const ASI_DEFAULT_GUARDRAILS = Object.freeze({
  gen: {
    lightsBuffer: [60, 120],
    maxEcpw: 9,
    flushDuration: 5,
    interval: [10, 60],
    minVwc: 0.25,
    duration: [6, 4],
    drainGoal: 0.05,
    maxFillMinutes: 90,
  },
  veg: {
    lightsBuffer: [60, 120],
    maxEcpw: 6,
    flushDuration: 5.5,
    interval: [15, 30],
    minVwc: 0.25,
    duration: [4, 5],
    drainGoal: 0.05,
    maxFillMinutes: 120,
  },
  mode: 'veg',
  value: 10
})

const ASIStartMsg = i18n`Start irrigating at approx.`
const ASIMaintainMsg = i18n`Maintain near field capacity until`
const ASIFieldCapacityMsg = i18n`to field capacity`
const defaultASIEndTime = i18n`end of irrigation window`
export const ASI_META = Object.freeze({
  1: {
    mode: 'GEN',
    get label() { return i18n`Maximum Generative` },
    description: (
      startTime,
      endTime = defaultASIEndTime
    ) => [
      [ASIStartMsg, startTime, ASIFieldCapacityMsg].join(' '),
      [ASIMaintainMsg, endTime].join(' ')
    ].join('\n'),
  },
  2: {
    mode: 'GEN',
    get label() { return i18n`Very High Generative` },
    description: (
      startTime,
      endTime = defaultASIEndTime
    ) => [
      [ASIStartMsg, startTime, ASIFieldCapacityMsg].join(' '),
      [ASIMaintainMsg, endTime].join(' ')
    ].join('\n'),
  },
  3: {
    mode: 'GEN',
    get label() { return i18n`High Generative` },
    description: (
      startTime,
      endTime = defaultASIEndTime
    ) => [
      [ASIStartMsg, startTime, ASIFieldCapacityMsg].join(' '),
      [ASIMaintainMsg, endTime].join(' ')
    ].join('\n'),
  },
  4: {
    mode: 'GEN',
    get label() { return i18n`Medium Generative` },
    description: (
      startTime,
      endTime = defaultASIEndTime
    ) => [
      [ASIStartMsg, startTime, ASIFieldCapacityMsg].join(' '),
      [ASIMaintainMsg, endTime].join(' ')
    ].join('\n'),
  },
  5: {
    mode: 'GEN',
    get label() { return i18n`Mild Generative` },
    description: (
      startTime,
      endTime = defaultASIEndTime
    ) => [
      [ASIStartMsg, startTime, ASIFieldCapacityMsg].join(' '),
      [ASIMaintainMsg, endTime].join(' ')
    ].join('\n'),
  },
  6: {
    mode: 'VEG',
    get label() { return i18n`Mild Vegetative` },
    description: (
      startTime,
      endTime = defaultASIEndTime
    ) => [
      [ASIStartMsg, startTime, ASIFieldCapacityMsg].join(' '),
      [ASIMaintainMsg, endTime].join(' ')
    ].join('\n'),
  },
  7: {
    mode: 'VEG',
    get label() { return i18n`Medium Vegetative` },
    description: (
      startTime,
      endTime = defaultASIEndTime
    ) => [
      [ASIStartMsg, startTime, ASIFieldCapacityMsg].join(' '),
      [ASIMaintainMsg, endTime].join(' ')
    ].join('\n'),
  },
  8: {
    mode: 'VEG',
    get label() { return i18n`High Vegetative` },
    description: (
      startTime,
      endTime = defaultASIEndTime
    ) => [
      [ASIStartMsg, startTime, ASIFieldCapacityMsg].join(' '),
      [ASIMaintainMsg, endTime].join(' ')
    ].join('\n'),
  },
  9: {
    mode: 'VEG',
    get label() { return i18n`Very High Vegetative` },
    description: (
      startTime,
      endTime = defaultASIEndTime
    ) => [
      [ASIStartMsg, startTime, ASIFieldCapacityMsg].join(' '),
      [ASIMaintainMsg, endTime].join(' ')
    ].join('\n'),
  },
  10: {
    mode: 'VEG',
    get label() { return i18n`Maximum Vegetative` },
    description: (
      startTime,
      endTime = i18n`end of the full irrigation window`
    ) => [
      [ASIStartMsg, startTime, ASIFieldCapacityMsg].join(' '),
      [ASIMaintainMsg, endTime].join(' ')
    ].join('\n'),
  },
})

export const DrawerContext = createContext({})

export const CONTROL_DAILY_JOURNAL_EVENT_TYPE = {
  SEQ_GROUP_CHANGE: 'SEQ_GROUP_CHANGE',
  PORT_ZONE_CHANGE: 'PORT_ZONE_CHANGE',
  EMERGENCY_SHOT: 'EMERGENCY_SHOT',
  RUN_ONCE: 'RUN_ONCE',
  SCHEDULE_CHANGE: 'SCHEDULE_CHANGE',
  SCHEDULE_PAUSED: 'SCHEDULE_PAUSED',
  SCHEDULE_RESUMED: 'SCHEDULE_RESUMED',
  SCHEDULE_DELETED: 'SCHEDULE_DELETED',
  IRRIGATION_STARTED: 'IRRIGATION_STARTED',
  IRRIGATION_COMPLETED: 'IRRIGATION_COMPLETED',
  P1_ENDED_DRAINAGE: 'P1_ENDED_DRAINAGE',
  P1_ENDED_TIME: 'P1_ENDED_TIME',
  P2_STARTED_DRAINAGE: 'P2_STARTED_DRAINAGE',
  P2_STARTED_TIME: 'P2_STARTED_TIME',
  AUTOMATED_SETTINGS_CHANGE: 'AUTOMATED_SETTINGS_CHANGE',
  EMERGENCY_SHOT_CHANGE: 'EMERGENCY_SHOT_CHANGE',
}

export const AUTOMATED_EVENT_TYPE = {
  P0: 'P0',
  P1: 'P1',
  P2: 'P2',
}

export const AUTOMATED_EVENT_TYPE_LABEL = {
  get P0() { return i18n`Flush Event` },
  get P1() { return i18n`Fill to first Drain Event` },
  get P2() { return i18n`Maintaining Field Capacity` },
}

export const AUTOMATED_EVENT_SHOT_DETAIL_REASON = {
  ADDITIONAL_P1: 'ADDITIONAL_P1',
  ECPW_SUPPLEMENTAL_P2: 'ECPW_SUPPLEMENTAL_P2',
  FIRST_P1: 'FIRST_P1',
  MAX_WAIT_P2: 'MAX_WAIT_P2',
  REFILL_THRESHOLD_P2: 'REFILL_THRESHOLD_P2',
  VWC_SUPPLEMENTAL_P2: 'VWC_SUPPLEMENTAL_P2',
}

export const AUTOMATED_IRRIGATION_SHOT_REASON_DATA_TYPE = {
  ECPW_SUPPLEMENTAL_P2: 'pore_ec',
  REFILL_THRESHOLD_P2: 'soil_moist',
  VWC_SUPPLEMENTAL_P2: 'soil_moist',
}
